import axios from 'axios';
import userConfigService from '@/services/UserConfigService';
import { getApiBase as resolveApiBase, getValidatedApiBase } from '@/utils/apiBase';

const API = axios.create({
  baseURL: resolveApiBase(),
  headers: {
    'Content-Type': 'application/json',
    // Authorization header will be set dynamically in a request interceptor below
  },
});

export function getApiBase(): string {
  return getValidatedApiBase();
}

// Ensure we always send the latest token from localStorage (in case it changed after module init)
API.interceptors.request.use((config) => {
  try {
    const token = localStorage.getItem('token') || '';
    if (config && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // ignore
  }
  return config;
});

// ------------ INTERFACES PRINCIPALES ------------
// Interfaces
export interface Project {
  id: number;
  name: string;
  done: boolean;
  archived: boolean;
  position: number;
  fk_client: number;
  fk_user: number;
  close_date_known?: boolean;
  expected_close_date?: string | null;
  amount_known?: boolean;
  expected_amount?: number | null;
  business_problem?: string | null;
  opportunity_source?: string | null;
  decision_maker_known?: boolean;
  loss_reason?: string | null;
  claim_summary?: string | null;
  claim_priority?: string | null;
  claim_opened_at?: string | null;
  claim_resolved_at?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface Client {
  id: number;
  comercial_name: string;
  nit: string;
  social_reason: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
}

// ACTIONS
export interface ProjectActionType {
  id: number;
  name: string;
  active?: boolean;
  funnel_stage?: string | null;
  funnel_order?: number | null;
}

export interface ProjectAction {
  id: number;
  fk_project_action_type: number;
  fk_project: number;
  fk_user?: number;

  planned: string; // "YYYY-MM-DD"
  executed?: string; // "YYYY-MM-DD" (opcional)
  createdAt?: string;
  updatedAt?: string;
  // Optional time fields (backend accepts ISO datetimes for start/end)
  start_time?: string;
  end_time?: string;
  // Optional done flag - some flows set this when action is completed
  done?: boolean;
  description?: string; // Descripción opcional
  google_attendees?: Array<{
    email: string;
    displayName?: string;
  }>;
  google_event_id?: string; // ID del evento en Google Calendar
  archived?: boolean;
  archived_at?: string | null;
  action_type_name?: string; // Name of the action type (from join)
  // Si la respuesta del backend incluye órdenes relacionadas a la acción (opcional)
  orders?: Array<{
    order_id?: number;
    document?: string;
    ready?: boolean;
    upload?: boolean;
    // otros campos posibles del payload del backend pueden añadirse si es necesario
  }>;
}

export type ProjectActionExecutionMode = 'done' | 'not_done' | 'reschedule';

export interface ExecuteProjectActionPayload {
  mode: ProjectActionExecutionMode;
  effective_at?: string | Date;
  reason?: string;
  create_next_action?: boolean;
  next_action_type_id?: number;
  next_effective_at?: string | Date;
  next_description?: string;
}

// MONEY
export interface ProjectTypeMoney {
  id: number;
  name: string;
}

export interface ProjectMoney {
  id: number;
  fk_project_type_money: number;
  fk_project: number;
  value: number;
}

// DATES
export interface ProjectDateType {
  id: number;
  name: string;
}

export interface ProjectDate {
  id: number;
  fk_project_type_date: number;
  fk_project: number;
  date: string; // "YYYY-MM-DD"
  description?: string;
  projectTypeDate?: string | null;
}

export interface ProjectInfoAction {
  id: number;
  planned?: string | null;
  executed?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  google_event_id?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  description?: string | null;
  archived?: boolean;
  archived_at?: string | null;
  fk_project_action_type: number;
  action_type_name?: string | null;
  orders?: any[];
}

export interface ProjectInfoDate {
  id: number;
  date: string;
  fk_project_type_date: number;
  date_type_name?: string | null;
  description?: string | null;
}

export interface ProjectInfoPayload {
  id: number;
  name: string;
  done: boolean;
  fk_user: number;
  fk_client: number;
  user_name?: string;
  client_nit?: string;
  client_comercial_name?: string;
  project_money?: number;
  order_quantity?: number;
  acomplished_action_quantity?: number;
  acomplished_percentage?: string;
  actions?: ProjectInfoAction[];
  dates?: ProjectInfoDate[];
  comments?: any[];
}

export interface ProjectInfoResult {
  project: Project;
  actions: ProjectAction[];
  dates: ProjectDate[];
  moneys: ProjectMoney[]; // mapeamos project_money a 1 ítem "TOTAL"
  meta: {
    totalMoney: number;
    orderQuantity: number;
    accomplishedActions: number;
    accomplishedPercent: number;
    clientName?: string;
    clientNit?: string;
    userName?: string;
  };
  // Tipos detectados desde la respuesta (para evitar otro fetch si quieres)
  inferredActionTypes?: ProjectActionType[];
  inferredDateTypes?: ProjectDateType[];
  inferredMoneyTypes?: ProjectTypeMoney[]; // incluye "TOTAL"
}

// ------------ CRUD PROJECT ------------

export async function fetchProjects(
  filters: Partial<Pick<Project, 'fk_user' | 'fk_client' | 'done' | 'name'>> = {},
): Promise<Project[]> {
  try {
    try {
      console.debug('fetchProjects request', filters);
    } catch {}
    const { data } = await API.get('/project', { params: filters });
    try {
      console.debug(
        'fetchProjects response',
        Array.isArray(data)
          ? { count: data.length }
          : Array.isArray(data.data)
            ? { count: data.data.length }
            : { raw: !!data },
      );
    } catch {}
    // Tu backend podría retornar data.data o directamente el array:
    return Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
  } catch (err) {
    try {
      console.debug('fetchProjects failed', filters, (err as any)?.message || err);
    } catch {}
    return [];
  }
}
export async function fetchProjectById(id: number): Promise<Project | null> {
  try {
    const { data } = await API.get(`/project/${id}`);
    const payload = data && (data.data || data);
    const p = Array.isArray(payload) ? payload[0] : payload;
    if (!p) return null;
    // Normalize to Project shape if possible
    return {
      id: p.id,
      name: p.name,
      done: Boolean(p.done),
      archived: Boolean(p.archived),
      position: Number(p.position) || 0,
      fk_client: Number(p.fk_client),
      fk_user: Number(p.fk_user),
      close_date_known: Boolean(p.close_date_known),
      expected_close_date: p.expected_close_date ? String(p.expected_close_date) : null,
      amount_known: Boolean(p.amount_known),
      expected_amount:
        p.expected_amount !== undefined && p.expected_amount !== null
          ? Number(p.expected_amount)
          : null,
      business_problem: p.business_problem ? String(p.business_problem) : null,
      opportunity_source: p.opportunity_source ? String(p.opportunity_source) : null,
      decision_maker_known: Boolean(p.decision_maker_known),
      loss_reason: p.loss_reason ? String(p.loss_reason) : null,
      claim_summary: p.claim_summary ? String(p.claim_summary) : null,
      claim_priority: p.claim_priority ? String(p.claim_priority) : null,
      claim_opened_at: p.claim_opened_at ? String(p.claim_opened_at) : null,
      claim_resolved_at: p.claim_resolved_at ? String(p.claim_resolved_at) : null,
    } as Project;
  } catch (err) {
    try {
      console.debug('fetchProjectById failed', id, (err as any)?.message || err);
    } catch {}
    return null;
  }
}
export async function createProject(
  payload: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<Project> {
  const name = String(payload?.name || '').trim();
  const fk_client = Number(payload?.fk_client);
  const fk_user = Number(payload?.fk_user);
  const closeKnown = Boolean(payload?.close_date_known);
  const amountKnown = Boolean(payload?.amount_known);
  const decisionKnown = Boolean(payload?.decision_maker_known);
  const expected_close_date = closeKnown ? payload?.expected_close_date || null : null;
  const expected_amount = amountKnown ? Number(payload?.expected_amount) || null : null;
  const business_problem =
    String(payload?.business_problem || '').trim() || 'Pendiente de diagnostico comercial';
  const opportunity_source = payload?.opportunity_source
    ? String(payload.opportunity_source).trim()
    : undefined;
  const loss_reason = payload?.loss_reason ? String(payload.loss_reason).trim() : undefined;
  const claim_summary = payload?.claim_summary ? String(payload.claim_summary).trim() : undefined;
  const claim_priority = payload?.claim_priority
    ? String(payload.claim_priority).trim()
    : undefined;
  const claim_opened_at = payload?.claim_opened_at || null;
  const claim_resolved_at = payload?.claim_resolved_at || null;

  if (
    !name ||
    !Number.isFinite(fk_client) ||
    fk_client <= 0 ||
    !Number.isFinite(fk_user) ||
    fk_user <= 0
  ) {
    throw new Error('Faltan nombre, cliente o vendedor para crear el proyecto');
  }
  if (closeKnown && !expected_close_date) {
    throw new Error('Falta la fecha de cierre estimada');
  }
  if (amountKnown && (expected_amount === null || expected_amount <= 0)) {
    throw new Error('Falta el monto estimado');
  }

  const normalizedPayload: any = {
    name,
    fk_client,
    fk_user,
    done: Boolean(payload?.done),
    close_date_known: closeKnown,
    expected_close_date,
    amount_known: amountKnown,
    expected_amount,
    decision_maker_known: decisionKnown,
    business_problem,
    opportunity_source,
    loss_reason,
    claim_summary,
    claim_priority,
    claim_opened_at,
    claim_resolved_at,
  };
  const { data } = await API.post('/project', normalizedPayload);
  return data.data;
}
export async function updateProject(id: number, payload: Partial<Project>): Promise<Project> {
  const { data } = await API.patch(`/project/${id}`, payload);
  return data.data;
}
export async function deleteProject(id: number): Promise<void> {
  await API.delete(`/project/${id}`);
}

// ------------ CRUD CLIENTES Y USUARIOS ------------

export async function fetchClients(): Promise<Client[]> {
  const { data } = await API.get('/client');
  // Asegura el shape, filtra si tu backend trae otra estructura
  const arr = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];

  return arr.map((c: any) => ({
    id: c.id,
    comercial_name: c.comercial_name || '',
    nit: c.nit || '',
    social_reason: c.social_reason || '',
  }));
}

export async function fetchUsers(): Promise<User[]> {
  const { data } = await API.get('/user');
  return Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
}

// ------------ CRUD ACCIONES (ACTIONS) ------------

export async function fetchProjectActionTypes(): Promise<ProjectActionType[]> {
  const base = getApiBase();
  const url = base ? `${base}/type-action?t=${Date.now()}` : `/type-action?t=${Date.now()}`;
  const { data } = await API.get(url);

  // Backend may return the array directly or wrapped in { data: [...] }
  if (Array.isArray(data)) return data;
  if (Array.isArray((data && (data.data || data)) as any)) return data.data || data;

  return [];
}

export async function fetchProjectActions(projectId: number): Promise<ProjectAction[]> {
  try {
    // use params so axios builds the query string and the backend receives fk_project correctly
    const { data } = await API.get('/project-action', { params: { fk_project: projectId } });
    // Accept either an array directly or an object with { data: [...] }
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.data)) return data.data;
    return [];
  } catch (err) {
    // Backend may return 500 when there are no actions for a project; treat as empty list
    try {
      // eslint-disable-next-line no-console
      console.debug(
        'fetchProjectActions failed for project',
        projectId,
        (err as any)?.message || err,
      );
    } catch {}
    return [];
  }
}
export async function createProjectAction(
  payload: Omit<ProjectAction, 'id'>,
): Promise<ProjectAction> {
  const { data } = await API.post('/project-action', payload);
  return data.data;
}
export async function updateProjectAction(
  id: number,
  payload: Partial<ProjectAction>,
): Promise<ProjectAction> {
  const { data } = await API.patch(`/project-action/${id}`, payload);
  return data.data;
}
export async function executeProjectAction(
  id: number,
  payload: ExecuteProjectActionPayload,
): Promise<{ action: ProjectAction; next_action?: ProjectAction | null }> {
  const { data } = await API.post(`/project-action/${id}/execute`, payload);
  return data.data || data;
}
export async function deleteProjectAction(id: number): Promise<void> {
  await API.delete(`/project-action/${id}`);
}

// ------------ CRUD MONEY ------------

export async function fetchProjectTypeMoneys(): Promise<ProjectTypeMoney[]> {
  const { data } = await API.get('/type-money');
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

export async function fetchProjectMoneys(projectId: number): Promise<ProjectMoney[]> {
  try {
    const { data } = await API.get(`/project-money?fk_project=${projectId}`);
    return Array.isArray(data.data) ? data.data : [];
  } catch (err) {
    try {
      // eslint-disable-next-line no-console
      console.debug(
        'fetchProjectMoneys failed for project',
        projectId,
        (err as any)?.message || err,
      );
    } catch {}
    return [];
  }
}
/** Toda la plata estimada visible para el usuario (el backend ya la acota por rol). */
export async function fetchAllProjectMoneys(): Promise<ProjectMoney[]> {
  try {
    const { data } = await API.get('/project-money');
    return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
  } catch {
    return [];
  }
}

export async function createProjectMoney(payload: Omit<ProjectMoney, 'id'>): Promise<ProjectMoney> {
  const { data } = await API.post('/project-money', payload);
  return data.data;
}
export async function updateProjectMoney(
  id: number,
  payload: Partial<ProjectMoney>,
): Promise<ProjectMoney> {
  const { data } = await API.patch(`/project-money/${id}`, payload);
  return data.data;
}
export async function deleteProjectMoney(id: number): Promise<void> {
  await API.delete(`/project-money/${id}`);
}

// ------------ CRUD FECHAS (DATES) ------------

export async function fetchProjectDateTypes(): Promise<ProjectDateType[]> {
  const { data } = await API.get('/type-date');
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  return [];
}

export async function fetchProjectDates(projectId: number): Promise<ProjectDate[]> {
  try {
    // Use query params (form-style) so backend receives ?fk_project=ID
    const { data } = await API.get('/project-date', { params: { fk_project: projectId } });
    return Array.isArray(data.data) ? data.data : [];
  } catch (err) {
    try {
      // eslint-disable-next-line no-console
      console.debug(
        'fetchProjectDates failed for project',
        projectId,
        (err as any)?.message || err,
      );
    } catch {}
    return [];
  }
}

/** Todas las fechas de proyectos visibles para el usuario actual. */
export async function fetchAllProjectDates(): Promise<ProjectDate[]> {
  try {
    const { data } = await API.get('/project-date');
    return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
  } catch {
    return [];
  }
}
export async function createProjectDate(payload: Omit<ProjectDate, 'id'>): Promise<ProjectDate> {
  const { data } = await API.post('/project-date', payload);
  return data.data;
}
export async function updateProjectDate(
  id: number,
  payload: Partial<ProjectDate>,
): Promise<ProjectDate> {
  const { data } = await API.patch(`/project-date/${id}`, payload);
  return data.data;
}
export async function deleteProjectDate(id: number): Promise<void> {
  await API.delete(`/project-date/${id}`);
}

// ------------ COMMENTS (PROJECT_COMMENT) ------------

export interface ProjectComment {
  id: number;
  fk_user: number;
  fk_project: number;
  content: string;
  author?: string;
  createdAt?: string;
  // optional full user object and a user photo url
  user?: User;
  photoUrl?: string;
}

export async function fetchProjectComments(projectId: number): Promise<ProjectComment[]> {
  try {
    const { data } = await API.get(`/project-comment?fk_project=${encodeURIComponent(projectId)}`);
    // normalize response shape
    const arr = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
    // Basic normalized comments
    const baseComments: ProjectComment[] = arr.map((c: any) => ({
      id: c.id || c._id,
      fk_user: c.fk_user || c.user_id || c.user || 0,
      fk_project: c.fk_project || c.project_id || 0,
      content: c.content || c.body || '',
      author: c.author || c.name || (c.user && c.user.name) || undefined,
      createdAt: c.createdAt || c.created_at || c.created || undefined,
    }));

    try {
      // Attempt to enrich comments with user data (name/photo) by fetching users
      const users = await fetchUsers().catch(() => []);
      const userMap: Record<number, User> = {};
      (users || []).forEach((u: any) => {
        if (u && (u.id || u.id === 0)) userMap[Number(u.id)] = u;
      });

      const mapped = await Promise.all(
        baseComments.map(async (cm) => {
          const u = userMap[Number(cm.fk_user)];
          let photoUrl: string | undefined = undefined;
          try {
            if (u && u.id !== null && u.id !== undefined) {
              const data = await userConfigService.getUserPhotoDataUrl(Number(u.id));
              if (data) photoUrl = data;
            }
          } catch (e) {
            // silent fallback
            photoUrl = undefined;
          }

          return {
            ...cm,
            user: u || undefined,
            author: cm.author || (u && u.name) || cm.author,
            photoUrl,
          };
        }),
      );

      return mapped;
    } catch (e) {
      // If enrichment fails, return base comments
      return baseComments;
    }
  } catch (err) {
    try {
      // If the backend returns a server error (500) or not found (404) when
      // there are no comments, treat it as "no comments" instead of
      // propagating an error. Also accept 204 No Content as empty.
      const status = (err as any)?.response?.status;
      if (status === 404 || status === 500 || status === 204) {
        // eslint-disable-next-line no-console
        console.debug('fetchProjectComments empty response', projectId, status);
        return [];
      }
      // Otherwise, log the error for debugging and fall back to empty list.
      try {
        console.debug('fetchProjectComments failed', projectId, (err as any)?.message || err);
      } catch {}
    } catch {}
    return [];
  }
}

export async function createProjectComment(
  payload: Omit<ProjectComment, 'id' | 'createdAt' | 'author'>,
): Promise<ProjectComment | null> {
  try {
    const { data } = await API.post('/project-comment', payload);
    const saved = data && data.data ? data.data : data;
    if (!saved) return null;
    return {
      id: saved.id || saved._id,
      fk_user: saved.fk_user || saved.user_id || 0,
      fk_project: saved.fk_project || saved.project_id || 0,
      content: saved.content || saved.body || '',
      author: saved.author || saved.name || (saved.user && saved.user.name) || undefined,
      createdAt: saved.createdAt || saved.created_at || saved.created || undefined,
    };
  } catch (err) {
    try {
      console.debug('createProjectComment failed', payload, (err as any)?.message || err);
    } catch {}
    return null;
  }
}

export async function updateProjectComment(
  id: string | number,
  payload: Partial<ProjectComment>,
): Promise<ProjectComment | null> {
  try {
    const { data } = await API.patch(`/project-comment/${encodeURIComponent(String(id))}`, payload);
    const saved = data && data.data ? data.data : data;
    if (!saved) return null;
    return {
      id: saved.id || saved._id,
      fk_user: saved.fk_user || saved.user_id || 0,
      fk_project: saved.fk_project || saved.project_id || 0,
      content: saved.content || saved.body || '',
      author: saved.author || saved.name || (saved.user && saved.user.name) || undefined,
      createdAt: saved.createdAt || saved.created_at || saved.created || undefined,
    };
  } catch (err) {
    try {
      console.debug('updateProjectComment failed', id, (err as any)?.message || err);
    } catch {}
    return null;
  }
}

/**
 * Borra un comentario de proyecto. El servidor solo lo permite al autor (o a un
 * administrador) y valida además el acceso al proyecto, así que un `false` aquí
 * suele significar "no tienes permiso" y no un fallo de red.
 */
export async function deleteProjectComment(id: string | number): Promise<boolean> {
  try {
    await API.delete(`/project-comment/${encodeURIComponent(String(id))}`);
    return true;
  } catch (err) {
    try {
      console.debug('deleteProjectComment failed', id, (err as any)?.message || err);
    } catch {}
    return false;
  }
}

function getUserIdFromStorage(): number | null {
  try {
    const v = localStorage.getItem('fk_user');
    if (!v) return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  } catch {
    return null;
  }
}

export async function getInactiveClients(userId?: number): Promise<Client[]> {
  const uid = userId ?? getUserIdFromStorage();
  if (uid === null || uid === undefined) {
    console.warn(
      'getInactiveClients: no se encontró fk_user en localStorage ni se pasó por parámetro.',
    );
    return [];
  }

  // Read token from localStorage and pass it explicitly to this request
  let config: any = {};
  try {
    const token = localStorage.getItem('token') || '';
    if (token) config.headers = { Authorization: `Bearer ${token}` };
  } catch {
    // ignore storage errors
  }

  const { data } = await API.get(`/inactive-clients/${uid}`, config);
  const arr = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
  return arr.map((c: any) => ({
    id: c.id,
    comercial_name: c.comercial_name || '',
    nit: c.nit || '',
    social_reason: c.social_reason || '',
  }));
}

export async function fetchProjectInfo(
  projectIdOrUserId: number,
): Promise<ProjectInfoResult | null> {
  try {
    try {
      console.debug('fetchProjectInfo request', projectIdOrUserId);
    } catch {}
    const { data } = await API.get(
      `/project-info/${encodeURIComponent(String(projectIdOrUserId))}`,
    );
    try {
      console.debug('fetchProjectInfo raw response', data);
    } catch {}
    const payload: ProjectInfoPayload = (data && data.data) || data;

    if (!payload || !payload.id) return null;

    const project: Project = {
      id: Number(payload.id),
      name: payload.name,
      done: Boolean(payload.done),
      archived: Boolean((payload as any).archived),
      position: 0, // Default position, can be updated from payload if available
      fk_user: Number(payload.fk_user),
      fk_client: Number(payload.fk_client),
      close_date_known: Boolean((payload as any).close_date_known),
      expected_close_date: (payload as any).expected_close_date
        ? String((payload as any).expected_close_date)
        : null,
      amount_known: Boolean((payload as any).amount_known),
      expected_amount:
        (payload as any).expected_amount !== undefined && (payload as any).expected_amount !== null
          ? Number((payload as any).expected_amount)
          : null,
      business_problem: (payload as any).business_problem
        ? String((payload as any).business_problem)
        : null,
      opportunity_source: (payload as any).opportunity_source
        ? String((payload as any).opportunity_source)
        : null,
      decision_maker_known: Boolean((payload as any).decision_maker_known),
      loss_reason: (payload as any).loss_reason ? String((payload as any).loss_reason) : null,
      claim_summary: (payload as any).claim_summary ? String((payload as any).claim_summary) : null,
      claim_priority: (payload as any).claim_priority
        ? String((payload as any).claim_priority)
        : null,
      claim_opened_at: (payload as any).claim_opened_at
        ? String((payload as any).claim_opened_at)
        : null,
      claim_resolved_at: (payload as any).claim_resolved_at
        ? String((payload as any).claim_resolved_at)
        : null,
    };

    // Acciones -> mapeo a tu interfaz existente
    const actions: ProjectAction[] = (payload.actions || []).map((a) => ({
      id: Number(a.id),
      fk_project_action_type: Number(a.fk_project_action_type),
      fk_project: Number(payload.id),
      planned: a.planned ? String(a.planned) : '',
      executed: a.executed ? String(a.executed) : undefined,
      start_time: a.start_time ? String(a.start_time) : undefined,
      end_time: a.end_time ? String(a.end_time) : undefined,
      google_event_id: a.google_event_id ? String(a.google_event_id) : undefined,
      createdAt: a.createdAt ? String(a.createdAt) : undefined,
      updatedAt: a.updatedAt ? String(a.updatedAt) : undefined,
      description: a.description ?? undefined,
      archived: Boolean(a.archived),
      archived_at: a.archived_at ? String(a.archived_at) : undefined,
      // Preserve orders payload when present so UI can show related orders
      orders: Array.isArray(a.orders)
        ? a.orders.map((o: any) => ({
            order_id: o.order_id ?? o.id,
            document: o.document,
            ready: o.ready,
            upload: o.upload,
            // keep other fields available if necessary
            ...o,
          }))
        : undefined,
    }));

    // Fechas -> mapeo a tu interfaz existente
    const dates: ProjectDate[] = (payload.dates || []).map((d) => ({
      id: Number(d.id),
      fk_project_type_date: Number(d.fk_project_type_date),
      fk_project: Number(payload.id),
      date: String(d.date),
      description: d.description ?? undefined,
    }));

    // Dinero -> el endpoint trae un total; lo exponemos como un solo registro "TOTAL"
    const totalMoney = Number(payload.project_money || 0);
    const moneys: ProjectMoney[] =
      totalMoney > 0
        ? [
            {
              id: 0, // sintético
              fk_project_type_money: 0, // tipo sintético "TOTAL"
              fk_project: Number(payload.id),
              value: totalMoney,
            },
          ]
        : [];

    // Tipos inferidos desde los nombres que vienen en la respuesta
    const inferredActionTypes: ProjectActionType[] = Array.from(
      new Map(
        (payload.actions || []).map((a) => [
          a.fk_project_action_type,
          {
            id: Number(a.fk_project_action_type),
            name: a.action_type_name || `Tipo ${a.fk_project_action_type}`,
          },
        ]),
      ).values(),
    );

    const inferredDateTypes: ProjectDateType[] = Array.from(
      new Map(
        (payload.dates || []).map((d) => [
          d.fk_project_type_date,
          {
            id: Number(d.fk_project_type_date),
            name: d.date_type_name || `Fecha ${d.fk_project_type_date}`,
          },
        ]),
      ).values(),
    );

    const inferredMoneyTypes: ProjectTypeMoney[] = [{ id: 0, name: 'TOTAL' }];

    const meta = {
      totalMoney,
      orderQuantity: Number(payload.order_quantity || 0),
      accomplishedActions: Number(payload.acomplished_action_quantity || 0),
      accomplishedPercent: Number(
        payload.acomplished_percentage ? String(payload.acomplished_percentage) : 0,
      ),
      clientName: payload.client_comercial_name,
      clientNit: payload.client_nit,
      userName: payload.user_name,
    };

    return {
      project,
      actions,
      dates,
      moneys,
      meta,
      inferredActionTypes,
      inferredDateTypes,
      inferredMoneyTypes,
    };
  } catch (err) {
    try {
      console.debug('fetchProjectInfo failed', projectIdOrUserId, (err as any)?.message || err);
    } catch {}
    return null;
  }
}

export async function getActiveClients(userId?: number): Promise<Client[]> {
  const uid = userId ?? getUserIdFromStorage();
  if (uid === null || uid === undefined) {
    console.warn(
      'getActiveClients: no se encontró fk_user en localStorage ni se pasó por parámetro.',
    );
    return [];
  }

  let config: any = {};
  try {
    const token = localStorage.getItem('token') || '';
    if (token) config.headers = { Authorization: `Bearer ${token}` };
  } catch {
    // ignore
  }

  const { data } = await API.get(`/active-clients/${uid}`, config);
  const arr = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
  return arr.map((c: any) => ({
    id: c.id,
    comercial_name: c.comercial_name || '',
    nit: c.nit || '',
    social_reason: c.social_reason || '',
  }));
}

// ------------ BULK: Projects with latest action (aggregated endpoint)
export interface ProjectWithLastAction extends Project {
  last_action?: ProjectAction | null;
  next_action?: ProjectAction | null;
  action_count?: number;
  executed_action_count?: number;
  pending_action_count?: number;
  project_money?: number;
  accomplished_percentage?: number;
  client_comercial_name?: string;
  client_social_reason?: string;
  client_nit?: string;
  client_business_unit?: string;
  client_branch?: string;
}

export async function fetchProjectsWithLastAction(
  fk_client: number,
): Promise<ProjectWithLastAction[]> {
  try {
    const { data } = await API.get(
      `/client-projects-with-last-action/${encodeURIComponent(String(fk_client))}`,
    );
    const arr = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
    return arr.map((p: any) => ({
      id: Number(p.id),
      name: p.name,
      done: Boolean(p.done),
      archived: Boolean(p.archived),
      position: Number(p.position) || 0,
      fk_client: Number(p.fk_client),
      fk_user: Number(p.fk_user),
      project_money:
        p.project_money !== undefined && p.project_money !== null
          ? Number(p.project_money)
          : undefined,
      accomplished_percentage:
        p.accomplished_percentage !== undefined && p.accomplished_percentage !== null
          ? Number(p.accomplished_percentage)
          : p.acomplished_percentage !== undefined && p.acomplished_percentage !== null
            ? Number(p.acomplished_percentage)
            : undefined,
      action_count: typeof p.action_count === 'number' ? p.action_count : undefined,
      executed_action_count:
        typeof p.executed_action_count === 'number' ? p.executed_action_count : undefined,
      pending_action_count:
        typeof p.pending_action_count === 'number' ? p.pending_action_count : undefined,
      client_comercial_name: p.client_comercial_name || undefined,
      client_social_reason: p.client_social_reason || undefined,
      client_nit: p.client_nit || undefined,
      client_business_unit: p.client_business_unit || undefined,
      client_branch: p.client_branch || undefined,
      claim_summary: p.claim_summary ? String(p.claim_summary) : undefined,
      claim_priority: p.claim_priority ? String(p.claim_priority) : undefined,
      claim_opened_at: p.claim_opened_at ? String(p.claim_opened_at) : undefined,
      claim_resolved_at: p.claim_resolved_at ? String(p.claim_resolved_at) : undefined,
      // attach last_action and next_action if present
      last_action: p.last_action || null,
      next_action: p.next_action || null,
    }));
  } catch (err) {
    try {
      console.debug('fetchProjectsWithLastAction failed', fk_client, (err as any)?.message || err);
    } catch {}
    return [];
  }
}

export async function fetchProjectsWithLastActionByUser(
  params: {
    userId?: number;
    clientIds?: number[];
    includeArchivedProjects?: boolean;
  } = {},
): Promise<ProjectWithLastAction[]> {
  try {
    const query: Record<string, any> = {};
    if (params.userId !== undefined && params.userId !== null) {
      query.fk_user = Number(params.userId);
    }

    const normalizedClientIds = (params.clientIds || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (normalizedClientIds.length > 0) {
      query.client_ids = normalizedClientIds.join(',');
    }

    if (params.includeArchivedProjects === true) {
      query.include_archived_projects = true;
    }

    const { data } = await API.get('/projects-with-last-action', { params: query });
    const arr = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];

    return arr.map((p: any) => ({
      id: Number(p.id),
      name: p.name,
      done: Boolean(p.done),
      archived: Boolean(p.archived),
      position: Number(p.position) || 0,
      fk_client: Number(p.fk_client),
      fk_user: Number(p.fk_user),
      project_money:
        p.project_money !== undefined && p.project_money !== null
          ? Number(p.project_money)
          : undefined,
      accomplished_percentage:
        p.accomplished_percentage !== undefined && p.accomplished_percentage !== null
          ? Number(p.accomplished_percentage)
          : p.acomplished_percentage !== undefined && p.acomplished_percentage !== null
            ? Number(p.acomplished_percentage)
            : undefined,
      action_count: typeof p.action_count === 'number' ? p.action_count : undefined,
      executed_action_count:
        typeof p.executed_action_count === 'number' ? p.executed_action_count : undefined,
      pending_action_count:
        typeof p.pending_action_count === 'number' ? p.pending_action_count : undefined,
      client_comercial_name: p.client_comercial_name || undefined,
      client_social_reason: p.client_social_reason || undefined,
      client_nit: p.client_nit || undefined,
      client_business_unit: p.client_business_unit || undefined,
      client_branch: p.client_branch || undefined,
      claim_summary: p.claim_summary ? String(p.claim_summary) : undefined,
      claim_priority: p.claim_priority ? String(p.claim_priority) : undefined,
      claim_opened_at: p.claim_opened_at ? String(p.claim_opened_at) : undefined,
      claim_resolved_at: p.claim_resolved_at ? String(p.claim_resolved_at) : undefined,
      last_action: p.last_action || null,
      next_action: p.next_action || null,
    }));
  } catch (err) {
    try {
      console.debug(
        'fetchProjectsWithLastActionByUser failed',
        params,
        (err as any)?.message || err,
      );
    } catch {}
    return [];
  }
}

export type FetchProjectActionsByUserParams =
  | number
  | {
      userId?: number;
      includeInactiveTypes?: boolean;
      archived?: boolean | 'all';
    };

export async function fetchProjectActionsByUser(
  params: FetchProjectActionsByUserParams,
): Promise<ProjectAction[]> {
  const normalized =
    typeof params === 'number'
      ? { userId: params, includeInactiveTypes: false, archived: false as boolean | 'all' }
      : {
          userId: params?.userId,
          includeInactiveTypes: Boolean(params?.includeInactiveTypes),
          archived: params?.archived ?? false,
        };

  const buildQuery = (archivedValue?: boolean) => {
    const query: Record<string, any> = {};
    if (
      normalized.userId !== undefined &&
      normalized.userId !== null &&
      Number.isFinite(Number(normalized.userId)) &&
      Number(normalized.userId) > 0
    ) {
      query.fk_user = Number(normalized.userId);
    }
    if (normalized.includeInactiveTypes) {
      query.include_inactive_types = true;
    }
    if (typeof archivedValue === 'boolean') {
      query.archived = archivedValue;
    }
    return query;
  };

  const normalizeResponse = (payload: any): ProjectAction[] => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  try {
    if (normalized.archived === 'all') {
      const [activeRes, archivedRes] = await Promise.all([
        API.get('/project-action', { params: buildQuery(false) }),
        API.get('/project-action', { params: buildQuery(true) }),
      ]);
      const merged = [...normalizeResponse(activeRes.data), ...normalizeResponse(archivedRes.data)];
      const dedup = new Map<number, ProjectAction>();
      merged.forEach((item: any) => {
        const id = Number(item?.id);
        if (Number.isFinite(id) && id > 0) dedup.set(id, item);
      });
      return Array.from(dedup.values());
    }

    const { data } = await API.get('/project-action', {
      params: buildQuery(Boolean(normalized.archived)),
    });
    return normalizeResponse(data);
  } catch (err) {
    try {
      console.debug('fetchProjectActionsByUser failed', normalized, (err as any)?.message || err);
    } catch {}
    return [];
  }
}

export interface SendProjectEmailPayload {
  toEmails: string[];
  ccEmails?: string[];
  bccEmails?: string[];
  subject?: string;
  message?: string;
}

export async function sendProjectEmail(
  projectId: number,
  payload: SendProjectEmailPayload,
): Promise<any> {
  const { data } = await API.post(`/project/${encodeURIComponent(String(projectId))}/send-email`, {
    to_emails: payload.toEmails,
    cc_emails: payload.ccEmails || [],
    bcc_emails: payload.bccEmails || [],
    subject: payload.subject || '',
    message: payload.message || '',
  });
  return data;
}
